import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { setUser } from '../../redux/actions/user'
import { updateAvatar } from '../../service/user'
import { setLoading } from '../../redux/actions/ui'
import { apiError, updateProfile } from '../../api/account'

import styles from './AccountSettings.module.css'

// Shows +15551234567 as (555) 123-4567
const prettyPhone = (phone) => {
  const d = String(phone || '')
    .replace(/\D/g, '')
    .replace(/^1(?=\d{10}$)/, '')
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : phone || ''
}

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')

const ProfileSection = ({ user }) => {
  const dispatch = useDispatch()
  const fileRef = useRef(null)
  const [form, setForm] = useState({
    fullname: user.fullname || '',
    email: user.email || '',
    phone: prettyPhone(user.phone),
    sms_account_opt_in: !!user.sms_account_opt_in,
    sms_marketing_opt_in: !!user.sms_marketing_opt_in,
  })
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('')
  const [saving, setSaving] = useState(false)

  const set = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [key]: value }))
    setStatus('')
  }

  const noPhone = !form.phone.trim()

  const save = (e) => {
    e.preventDefault()
    const next = {}
    if (!form.fullname.trim()) next.fullname = 'Enter your name'
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'Enter a valid email'
    setErrors(next)
    if (Object.keys(next).length) return
    setSaving(true)
    updateProfile({
      fullname: form.fullname.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      sms_account_opt_in: !noPhone && form.sms_account_opt_in,
      sms_marketing_opt_in: !noPhone && form.sms_marketing_opt_in,
    })
      .then(({ data }) => {
        dispatch(setUser({ ...user, ...data }))
        setForm((f) => ({
          ...f,
          phone: prettyPhone(data.phone),
          sms_account_opt_in: !!data.sms_account_opt_in,
          sms_marketing_opt_in: !!data.sms_marketing_opt_in,
        }))
        setStatus('Saved')
      })
      .catch((err) => {
        const fields = err?.response?.data?.fields
        if (fields) setErrors(fields)
        setStatus(fields ? '' : apiError(err, 'We could not save your profile. Please try again.'))
      })
      .finally(() => setSaving(false))
  }

  const changePhoto = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    updateAvatar(file)(dispatch)
      .catch(() => setStatus('That photo could not be uploaded. Try a JPG or PNG.'))
      .finally(() => {
        dispatch(setLoading(false))
        e.target.value = ''
      })
  }

  return (
    <section id="profile" className={styles.card} aria-labelledby="profile-title">
      <h2 id="profile-title" className={styles.h2}>
        Profile
      </h2>
      <p className={styles.lead}>How you appear to your family groups.</p>
      <form onSubmit={save} noValidate>
        <div className={styles.row} style={{ gap: 24, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            {user.avatar ? (
              <img src={user.avatar} alt="" className={styles.avatar} />
            ) : (
              <div className={styles.avatar} aria-hidden="true">
                {initials(user.fullname)}
              </div>
            )}
            <button
              type="button"
              className={styles.btnLink}
              onClick={() => fileRef.current?.click()}
            >
              Change photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={changePhoto} />
          </div>
          <div className={`${styles.grid2} ${styles.grow}`}>
            <label className={styles.label}>
              Full name
              <input
                className={`${styles.input} ${errors.fullname ? styles.bad : ''}`}
                value={form.fullname}
                onChange={set('fullname')}
                autoComplete="name"
              />
              {errors.fullname ? (
                <span className={styles.fieldError}>{errors.fullname}</span>
              ) : null}
            </label>
            <label className={styles.label}>
              Email
              <input
                className={`${styles.input} ${errors.email ? styles.bad : ''}`}
                type="email"
                value={form.email}
                onChange={set('email')}
                autoComplete="email"
              />
              {errors.email ? <span className={styles.fieldError}>{errors.email}</span> : null}
            </label>
            <label className={styles.label}>
              Mobile phone (optional)
              <input
                className={`${styles.input} ${errors.phone ? styles.bad : ''}`}
                type="tel"
                inputMode="tel"
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={set('phone')}
                autoComplete="tel-national"
              />
              {errors.phone ? <span className={styles.fieldError}>{errors.phone}</span> : null}
            </label>
            <div className={`${styles.full} ${styles.list}`}>
              <label className={styles.check} style={noPhone ? { opacity: 0.55 } : undefined}>
                <input
                  type="checkbox"
                  checked={!noPhone && form.sms_account_opt_in}
                  disabled={noPhone}
                  onChange={set('sms_account_opt_in')}
                />
                <span>
                  Text me about my account and orders. For example, when a family member adds a
                  recipe or your cookbook ships.
                </span>
              </label>
              <label className={styles.check} style={noPhone ? { opacity: 0.55 } : undefined}>
                <input
                  type="checkbox"
                  checked={!noPhone && form.sms_marketing_opt_in}
                  disabled={noPhone}
                  onChange={set('sms_marketing_opt_in')}
                />
                <span>Text me Morsel news and offers. A few messages a month at most.</span>
              </label>
              <p className={styles.small} style={{ margin: 0 }}>
                By ticking a box you agree to receive texts from Morsel at this number. Consent is
                not a condition of purchase. Message and data rates may apply. Reply STOP to
                unsubscribe or HELP for help. See our{' '}
                <a href="https://getmorsel.com/privacy-policy/" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://getmorsel.com/terms-of-service/" target="_blank" rel="noreferrer">
                  Terms
                </a>
                .
              </p>
            </div>
          </div>
        </div>
        <div className={styles.row} style={{ marginTop: 20, justifyContent: 'flex-end' }}>
          {status ? (
            <span className={status === 'Saved' ? styles.saved : styles.error} role="status">
              {status}
            </span>
          ) : null}
          <button className={styles.btnPrimary} disabled={saving}>
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </div>
      </form>
    </section>
  )
}

export default ProfileSection
